import { DateTime } from "luxon"
import { DateRangePreset } from "../ui/activity/date-filter"
import { TabDateFilter } from "../../types/engagements"

export const FORMAT_ADVISOR_DATE = "MMM dd, yyyy · h:mm a"

export const formatAdvisorDate = (iso: string): string =>
  DateTime.fromISO(iso).toFormat(FORMAT_ADVISOR_DATE)

export const formatShortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  })

export const DEFAULT_MONTH_FORMAT = "LLLL"
export const DEFAULT_DATE_FORMAT = "MM-dd-yyyy"
export const DEFAULT_DATE_SLASH_FORMAT = "MM/dd/yyyy"
export const DEFAULT_DATETIME_FORMAT = "LLL dd 'at' h:mm a"
export const DEFAULT_DATETIME_LONG_FORMAT = "LLL dd yyyy 'at' h:mm a"
export const DEFAULT_DATETIME_SECONDS_FORMAT = "LLL dd yyyy 'at' h:mm:ss a"
export const DEFAULT_DATETIME_SECONDS_SLASH_FORMAT =
  "LL/dd/yyyy 'at' hh:mm:ss a"
export const DEFAULT_DATETIME_COMMA_FORMAT = "dd LLL yyyy, hh:mm a"
export const DEFAULT_DATETIME_DAY_FORMAT = "LL-dd-yyyy, EEEE 'at' hh:mm:ss a"

export const formatDateTimeObj = (
  dateTimeObj: DateTime,
  format?: string
): string => {
  const inThisYear = dateTimeObj.year === DateTime.now().year
  return dateTimeObj.toFormat(
    format ||
      (inThisYear ? DEFAULT_DATETIME_FORMAT : DEFAULT_DATETIME_LONG_FORMAT)
  )
}

export const formatDateTimeFromISO = (val: string, format?: string): string => {
  const dateTimeObj = DateTime.fromISO(val)
  return formatDateTimeObj(dateTimeObj, format)
}

export const formatDateTimeFromMillis = (
  val: number,
  format?: string
): string => {
  const dateTimeObj = DateTime.fromMillis(val)
  return formatDateTimeObj(dateTimeObj, format)
}

export const formatDateTimeFromTimestamp = (
  val: string | number,
  format?: string
): string => {
  if (Number.isNaN(parseInt(val?.toString()))) return ""
  return formatDateTimeFromMillis(parseInt(val?.toString()) * 1000, format)
}

export const getDateValueFromString = (
  date: string,
  format: string = DEFAULT_DATE_FORMAT
): string => (date && date !== "" ? formatDateTimeFromISO(date, format) : "")

export const getMonth = (year: number, monthNumber: number): string =>
  DateTime.local(year, monthNumber).toFormat(DEFAULT_MONTH_FORMAT)

export interface DateRangeTimestamps {
  min: number | undefined
  max: number | undefined
}

export interface DateRangeTimestampsOptions {
  unit?: "seconds" | "milliseconds"
}

/**
 * Converts a date range filter to timestamp boundaries
 */
export const dateRangeToTimestamps = (
  range: DateRangePreset,
  customStartDate: Date | null | undefined,
  customEndDate: Date | null | undefined,
  options: DateRangeTimestampsOptions = { unit: "seconds" }
): DateRangeTimestamps => {
  const now = DateTime.now()
  const { unit = "seconds" } = options

  const toTimestamp = (dt: DateTime): number => {
    return unit === "seconds" ? Math.floor(dt.toSeconds()) : dt.toMillis()
  }

  switch (range) {
    case "today":
      return {
        min: toTimestamp(now.startOf("day")),
        max: toTimestamp(now.endOf("day"))
      }
    case "last_7":
      return {
        min: toTimestamp(now.minus({ days: 7 }).startOf("day")),
        max: toTimestamp(now.endOf("day"))
      }
    case "last_30":
      return {
        min: toTimestamp(now.minus({ days: 30 }).startOf("day")),
        max: toTimestamp(now.endOf("day"))
      }
    case "last_90":
      return {
        min: toTimestamp(now.minus({ days: 90 }).startOf("day")),
        max: toTimestamp(now.endOf("day"))
      }
    case "custom":
      if (customStartDate && customEndDate) {
        return {
          min: toTimestamp(DateTime.fromJSDate(customStartDate).startOf("day")),
          max: toTimestamp(DateTime.fromJSDate(customEndDate).endOf("day"))
        }
      }
      return { min: undefined, max: undefined }
    case "all":
    default:
      return { min: undefined, max: undefined }
  }
}

/**
 * Convenience function that accepts a TabDateFilter object
 */
export const dateFilterToTimestamps = (
  dateFilter: TabDateFilter,
  options?: DateRangeTimestampsOptions
): DateRangeTimestamps => {
  return dateRangeToTimestamps(
    dateFilter.range,
    dateFilter.customStartDate,
    dateFilter.customEndDate,
    options
  )
}
